
import { PropType, computed, defineComponent, watch, ref, getCurrentInstance, onMounted } from 'vue'
import isUndefined from 'lodash/isUndefined'
import first from 'lodash/first'
import messages from '@dataheroes/messages'
import AdvancedMessageEditor from '../../../../ui/AdvancedMessageEditor.vue'
import useRouter from '../../../../../hooks/useRouter'
import { Connector } from '@root/src/types/main'
import { CONNECTOR_TYPES } from '@root/src/vars/general'
import TextInputGroup from '@root/src/components/ui/TextInputGroup.vue'
import BaseInputOutlined from '@root/src/components/inputs/BaseInputOutlined.vue'
import CustomReactionsStep from './CustomReactionsStep.vue'
import SmsTextEditor from '../../SmsTextEditor.vue'
import { getAdditionalServices } from '@root/src/api/networkFixer'

export default defineComponent({
    props: {
        value: {
            type: Object,
            required: true,
        },
        triggerIdFromQuery: {
            type: String,
            required: true,
        },
        answersStatus: {
            type: String,
            required: false,
        },
        event: {
            type: String,
            required: false,
        },
        sendToStaff: {
            type: Boolean,
            required: false,
        },
        mode: {
            type: String,
            required: true,
        },
        app: {
            type: Object,
            required: true,
        },
    },
    components: { AdvancedMessageEditor, TextInputGroup, BaseInputOutlined, CustomReactionsStep, SmsTextEditor },
    setup(props, { emit }) {
        const { route } = useRouter()
        const root = getCurrentInstance()!.proxy
        const project = computed(() => root.$store.getters.projectById(route.value.params.id))
        const yClientsConnector = computed<Connector>(() =>
            root.$store.getters
                .projectById(root.$router.currentRoute.params.id)
                .connectors.find((el: Connector) => el.connectorType === CONNECTOR_TYPES.yClients)
        )
        const dikidiConnector = computed<Connector>(() =>
            root.$store.getters
                .projectById(root.$router.currentRoute.params.id)
                .connectors.find((el: Connector) => el.connectorType === CONNECTOR_TYPES.dikidi)
        )
        const triggerValue = computed({
            get: () => props.value,
            set: (newValue) => {
                emit('update-trigger', newValue);
            },
        });
        const updateTrigger = (value) => {
            emit('update-trigger', value)
        }
        const trigger = computed(() => route.value.query.id)
        const activeTab = ref(0)
        // вкладка канала: 0 — мессенджеры, 1 — SMS (показываем всегда, без гейтинга по каналу)
        const channelTab = ref(0)
        // отдельный короткий текст под SMS — одно поле на триггер (value.shortText)
        const updateShortText = (val: string) => {
            emit('input', Object.assign({}, props.value, { shortText: val }))
        }
        // содержимое {информация-о-записях} — общее поле триггера (template), одно на все каналы
        const updateSmsTemplate = (val: string) => {
            emit('input', Object.assign({}, props.value, { template: val }))
        }
        const isInputText = ref(false)
        const isInputTemplate = ref(false)
        const additionalServicesVarsAdded = ref(false)
        const templateVarsList = computed(() => {
            const vars = ['service', 'date', 'staff.name', 'date.withoutDateButWithTime', 'date.onlyDate']
            if (yClientsConnector.value) {
                vars.push('service.withPrice', 'service.backSlash', 'staff.specialization')
            }
            return vars
        })

        const tabs = [
            {
                tabName: 'через администратора',
                key: 'admin',
                show: true,
            },
            {
                tabName: 'онлайн запись',
                key: 'online',
                show: true,
            },
        ]
        const tabsCustom = [
            { name: 'Текст', num: 0, id: 'text' },
            { name: 'Ответы', num: 1, id: 'answers' },
        ]
        const activeTabNum = ref(tabsCustom.find(el => el.id === 'text')?.num)
        watch(
            () => props.value.isOnlineOtherText,
            (v: boolean) => {
                if (!v) {
                    activeTab.value = 0
                }
            }
        )
        const showDifferentMessagesTabs = computed(() => {
            return ['afterRecord', 'afterRecordNew', 'afterRecordPrev'].includes(trigger.value as string)
        })
        const getTextKey = () => {
            const activeTabKey = tabs[activeTab.value].key
            const textKey = activeTabKey === 'admin' ? 'text' : 'textForOnline'
            return textKey
        }

        const getTemplateKey = () => {
            const activeTabKey = tabs[activeTab.value].key
            const templateKey = activeTabKey === 'admin' ? 'template' : 'templateForOnline'
            return templateKey
        }
        const getText = () => {
            const key = getTextKey()
            return props.value[key] ?? ''
        }
        const updateText = (val: string) => {
            if (!isInputText.value) {
                isInputText.value = true
            }
            const key = getTextKey()
            emit('input', Object.assign({}, props.value, { [key]: val }))
        }
        const getTemplate = () => {
            const key = getTemplateKey()
            return props.value[key] ?? ''
        }
        const updateTemplate = (val: string) => {
            if (!isInputTemplate.value) {
                isInputTemplate.value = true
            }
            const key = getTemplateKey()
            emit('input', Object.assign({}, props.value, { [key]: val }))
        }
        const updateBonusInfo = (val: string) => {
            emit('input', Object.assign({}, props.value, { bonusInfo: val }))
        }
        const textAdditionalVars = computed(() => {
            const messagevars = [
                { id: 'компания-наименование', name: 'компания-наименование' },
                { id: 'компания-адрес', name: 'компания-адрес' },
                { id: 'компания-телефон', name: 'компания-телефон' },
            ]
            
            if (yClientsConnector.value) {
                messagevars.push(
                    { id: 'отображаемое-имя', name: 'отображаемое-имя' },
                    { id: 'ссылка-на-отзыв', name: 'ссылка-на-отзыв' },
                    { id: 'ссылка-на-информацию-записи', name: 'ссылка-на-информацию-записи' },
                    { id: 'общая-стоимость', name: 'общая-стоимость' },
                )
            }
            if (props.value.bonusInfo) {
                messagevars.push(
                    { id: 'начислено-бонусов', name: 'начислено-бонусов' },
                    { id: 'списано-бонусов', name: 'списано-бонусов' },
                    { id: 'баланс-бонусов', name: 'баланс-бонусов' }
                )
            }
            if (yClientsConnector.value && props.value.sendToStaff && props.value.event === 'notificationBefore2Hours' && additionalServicesVarsAdded.value) {
                messagevars.push(
                    {id: 'цикл-возврата', name: 'цикл-возврата'},
                    {id: 'товары', name: 'товары'},
                    {id: 'товары-и-месяцы', name: 'товары-и-месяцы'},
                    {id: 'дополнительные-услуги', name: 'дополнительные-услуги'},
                    {id: 'дополнительные-услуги-и-месяцы', name: 'дополнительные-услуги-и-месяцы'},
                    {id: 'дата-последней-покупки', name: 'дата-последней-покупки'},
                    {id: 'дата-последнего-визита', name: 'дата-последнего-визита'},
                    {id: 'месяцев-с-последнего-посещения', name: 'месяцев-с-последнего-посещения'},
                    {id: 'месяцев-с-последней-покупки', name: 'месяцев-с-последней-покупки'},
                )
            }
            return messagevars
        })
        const textVarsList = ['recordData', 'client.name']
        // переменные SMS-поля — как у ОСНОВНОГО текста: recordData/client.name
        // (= {информация-о-записях}/{имя-клиента}) + связочные (компания/ссылки/бонусы).
        // Шаблонные переменные (услуги/дата/мастер) НЕ здесь — они только в блоке
        // «Переменная {информация-о-записях}» (templateVarsList), как в обычном скрипте.
        const smsVarsList = computed(() => {
            const base = messages.vars.vars
                .filter((el: any) => textVarsList.includes(el.id))
                .map((el: any) => ({ id: first(el.aliases), name: first(el.aliases) }))
            return [...base, ...textAdditionalVars.value]
        })
        const answersMessageVars = yClientsConnector.value
            ? [{ id: 'ссылка-на-отзыв', name: 'ссылка-на-отзыв' }]
            : []
        const update = (reaction: string, val: string, key: string) => {
            emit(
                'input',
                Object.assign({}, props.value, {
                    [key]: Object.assign({}, props.value[key], { [reaction]: val }),
                })
            )
        }
        const text = computed(() => props.value.text)
        const warningText = computed(() => {
            if(props.triggerIdFromQuery.startsWith('custom_') && isInputText && (!props.value.text || props.value.text?.trim().length < 3)) {
                return 'Нужно ввести более 2-х символов'
            } else if (props.value.text.includes('{отображаемое-имя}')) {
                return 'Внимание! Переменная {отображаемое-имя} подставит имя клиента в точности, как он записан в CRM. Не используйте ее, если клиенты подписаны с прозвищами или внутренними комменатриями'
            } else {
                return ''
            }
        })
        const answersList = computed(() => props.value.answersList)
        watch(
            () => text.value,
            v => {
                const exp = () => {
                    return !(
                        !props.value.text ||
                        props.value.text.trim().length < 3 ||
                        (props.value.text &&
                            props.value.text.includes('{информация-о-записях}') &&
                            (!props.value.template || props.value.template.trim().length < 3))
                    )
                }
                if (props.triggerIdFromQuery.startsWith('custom_')) {
                    emit('validation-updated', exp())
                }
            },
            { deep: true, immediate: true }
        )
        onMounted(async () => {
            if (yClientsConnector.value) {
                const res = await getAdditionalServices({projectId: project.value.id })
                if (!res?.error && res?.data?.additionalServices?.length > 0) {
                    additionalServicesVarsAdded.value = true
                }
            }
        })
        return {
            updateText,
            textVarsList,
            textAdditionalVars,
            updateBonusInfo,
            trigger,
            isUndefined,
            showDifferentMessagesTabs,
            tabs,
            activeTab,
            getText,
            getTemplate,
            updateTemplate,
            templateVarsList,
            isInputText,
            isInputTemplate,
            tabsCustom,
            activeTabNum,
            triggerValue,
            yClientsConnector,
            answersMessageVars,
            update,
            updateTrigger,
            answersList,
            dikidiConnector,
            additionalServicesVarsAdded,
            warningText,
            channelTab,
            updateShortText,
            updateSmsTemplate,
            smsVarsList,
        }
    },
})
